import { Formik } from 'formik';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@src/design-system/dialog';
import { Button } from '@src/design-system/button';

import reminderApi, {
  type TReminderPayload,
} from '@src/features/reminders/services/reminder-api';
import { REMINDER_TYPES } from '@src/shared/types/reminder';
import type { TReminderTemplate } from '@src/shared/types/reminder-template';

import {
  duplicateReminderInitialValuesFromReminder,
  editStageReminderInitialValuesFromReminder,
} from '../../utils/duplicate-reminder-initial-values-from-reminder';
import { createStageReminderInitialValuesFromRule } from '../../utils/stage-reminder-initial-values-from-rule';
import {
  createBulkStageReminderInitialValues,
  createBulkStageReminderValidationSchema,
  createLeadsReminderInitialValues,
  createLeadsReminderValidationSchema,
  createParticipantsReminderInitialValues,
  createParticipantsReminderValidationSchema,
  createReminderInitialValues,
  createReminderSchema,
  createStageReminderInitialValues,
  createStageReminderValidationSchema,
  type TCreateReminderFormValues,
} from './schema';
import type { TReminder } from '@src/shared/types/reminder';
import type { TRule } from '@src/shared/types/rule';
import { getCurrentScheduleFields } from '../../utils/get-current-schedule-fields';
import { CreateReminderAudienceSection } from './variants/audience-section';
import type {
  TBulkParticipantsReminderConfig,
  TBulkStageReminderConfig,
  TCreateReminderVariant,
} from './variants/types';
import { CreateReminderDetailsSection } from './create-reminder-details-section';
import { CreateReminderScheduleSection } from './create-reminder-schedule-section';
import { StageReminderDetailsSection } from './stage/stage-reminder-details-section';
import { StageRepetitionSection } from './stage/stage-repetition-section';
import { LivePreview } from '@src/shared/features/reminders/live-preview';
import { Send2 } from 'iconsax-react';
import { dayjsUtils } from '@src/shared/utils/helpers/dayjs';

function templateIdFromValues(
  template: TCreateReminderFormValues['template']
): number | undefined {
  if (
    template &&
    typeof template === 'object' &&
    'id' in template &&
    typeof (template as TReminderTemplate).id === 'number'
  ) {
    return (template as TReminderTemplate).id;
  }
  return undefined;
}

export function CreateReminderDialog({
  children,
  duplicateFrom,
  reminder,
  rule,
  variant = 'bulk',
  bulkStageConfig,
  participantsConfig,
  onSuccess,
}: {
  children: React.ReactNode;
  duplicateFrom?: TReminder;
  reminder?: TReminder;
  /** Stage variant only: pre-fills the reminder with the rule it is added from. */
  rule?: TRule;
  variant?: TCreateReminderVariant;
  bulkStageConfig?: TBulkStageReminderConfig;
  participantsConfig?: TBulkParticipantsReminderConfig;
  onSuccess?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const { id: programId = '' } = useParams();

  const { mutate: createReminder, isPending: isCreating } =
    reminderApi.useCreate();
  const { mutate: updateReminder, isPending: isUpdating } =
    reminderApi.useUpdate();
  const isEditMode = Boolean(reminder);
  const isStageVariant = variant === 'stage' || isEditMode;
  const isBulkStageVariant = variant === 'bulkStage';
  const isParticipantsVariant = variant === 'participants';
  const isPending = isEditMode ? isUpdating : isCreating;

  const initialValues: TCreateReminderFormValues = isEditMode
    ? editStageReminderInitialValuesFromReminder(reminder!)
    : duplicateFrom
      ? duplicateReminderInitialValuesFromReminder(duplicateFrom)
      : variant === 'leads'
        ? createLeadsReminderInitialValues()
        : isParticipantsVariant
          ? createParticipantsReminderInitialValues()
          : isBulkStageVariant
            ? createBulkStageReminderInitialValues(
                bulkStageConfig?.defaultStageIds ?? []
              )
            : isStageVariant
              ? rule
                ? createStageReminderInitialValuesFromRule(rule)
                : createStageReminderInitialValues()
              : createReminderInitialValues();

  const validationSchema =
    variant === 'leads' && !isEditMode
      ? createLeadsReminderValidationSchema()
      : isParticipantsVariant
        ? createParticipantsReminderValidationSchema()
        : isBulkStageVariant
          ? createBulkStageReminderValidationSchema()
          : isStageVariant
            ? createStageReminderValidationSchema()
            : createReminderSchema;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) setFormKey((k) => k + 1);
  };

  const handleSubmit = (values: TCreateReminderFormValues) => {
    const templateId = templateIdFromValues(values.template);
    const isSpecificDateStage =
      isStageVariant && values.type === REMINDER_TYPES.specificDate;

    let dateIso: string | undefined;
    let timezone: string | undefined;

    if (!isStageVariant || isSpecificDateStage) {
      const {
        scheduledDate,
        scheduledTime,
        timezone: tz,
      } = isSpecificDateStage
        ? values
        : values.scheduleDelivery
          ? values
          : getCurrentScheduleFields();

      const localParsed = dayjsUtils(`${scheduledDate}T${scheduledTime}`);
      dateIso = localParsed.format('YYYY-MM-DDTHH:mm:ss');
      timezone = tz;

      if (!dateIso) {
        toast.error(
          'Could not build scheduled time. Check date, time, and timezone.'
        );
        return;
      }
    }

    const payload: TReminderPayload = isStageVariant
      ? {
          name: values.name,
          content: values.content,
          subject: values.subject ?? undefined,
          notification_channel: values.notification_channel,
          type: values.type as string,
          variables: values.variables,
          attachments: values.attachments?.map((a) => a.id) ?? [],
          program: +programId,
          ...(templateId != null && { template: templateId }),
          ...(values.stages?.length && { stages: values.stages }),
          ...(values.rule && { rule: values.rule }),
          ...(values.question && { question: values.question }),
          ...(values.trigger_type && { trigger_type: values.trigger_type }),
          ...(values.type === REMINDER_TYPES.otherRules &&
            values.repeatSending &&
            values.frequency != null &&
            values.frequency !== '' && { frequency: values.frequency }),
          ...(values.type === REMINDER_TYPES.otherRules &&
            values.repeatSending &&
            values.frequency_count != null &&
            values.frequency_count !== 0 && {
              frequency_count: values.frequency_count,
            }),
          ...(values.is_rule_achieved && {
            is_rule_achieved: values.is_rule_achieved === 'ACHIEVED',
          }),
          ...(values.type === REMINDER_TYPES.otherRules &&
            values.rule && { rule_choices: values.rule_choices ?? [] }),
          ...(values.flight_type && {
            flight_type: values.flight_type,
          }),
          ...(values.submission_date_type && {
            submission_date_type: values.submission_date_type,
          }),
          ...(isSpecificDateStage &&
            dateIso &&
            timezone && { date: dateIso, timezone }),
        }
      : isBulkStageVariant
        ? {
            name: values.name,
            content: values.content,
            subject: values.subject ?? undefined,
            notification_channel: values.notification_channel,
            date: dateIso!,
            timezone: timezone!,
            type: REMINDER_TYPES.bulkStage,
            program: bulkStageConfig?.programId,
            variables: values.variables,
            attachments: values.attachments?.map((a) => a.id) ?? [],
            ...(templateId != null && { template: templateId }),
            ...((values.selectedMemberIds?.length ?? 0) > 0
              ? { members: values.selectedMemberIds }
              : values.stages?.length
                ? { stages: values.stages }
                : {}),
            ...(values.view &&
              typeof values.view === 'object' &&
              'id' in values.view &&
              values.view.id != null && { view: values.view.id }),
          }
        : isParticipantsVariant
          ? {
              name: values.name,
              content: values.content,
              subject: values.subject ?? undefined,
              notification_channel: values.notification_channel,
              date: dateIso!,
              timezone: timezone!,
              type: REMINDER_TYPES.bulkApplication,
              program: participantsConfig?.programId,
              variables: values.variables,
              attachments: values.attachments?.map((a) => a.id) ?? [],
              ...(templateId != null && { template: templateId }),
              ...((values.selectedMemberIds?.length ?? 0) > 0 && {
                members: values.selectedMemberIds,
              }),
              ...(values.view &&
                typeof values.view === 'object' &&
                'id' in values.view &&
                values.view.id != null && { view: values.view.id }),
            }
          : {
              name: values.name,
              content: values.content,
              subject: values.subject ?? undefined,
              notification_channel: values.notification_channel,
              date: dateIso!,
              timezone: timezone!,
              variables: values.variables,
              attachments: values.attachments?.map((a) => a.id) ?? [],
              ...(values.view &&
                typeof values.view === 'object' &&
                'id' in values.view &&
                values.view.id != null && { view: values.view.id }),
              ...(templateId != null && { template: templateId }),
              ...(variant === 'leads'
                ? {
                    type: REMINDER_TYPES.bulkLead,
                    ...((values.selectedLeadIds?.length ?? 0) > 0 && {
                      leads: values.selectedLeadIds,
                    }),
                  }
                : {
                    type:
                      values.audienceGroup === 'leads'
                        ? REMINDER_TYPES.bulkLead
                        : REMINDER_TYPES.bulkApplication,
                    ...(values.audienceGroup === 'program' &&
                      values.program?.id != null && {
                        program: values.program.id,
                      }),
                  }),
            };

    const onMutationSuccess = () => {
      onSuccess?.();
      handleOpenChange(false);
    };

    if (isEditMode && reminder) {
      updateReminder(
        {
          id: reminder.id,
          program: +programId,
          ...payload,
        },
        { onSuccess: onMutationSuccess }
      );
      return;
    }

    createReminder(payload, { onSuccess: onMutationSuccess });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className='flex h-[95%] w-full max-w-[78%] flex-col gap-0 overflow-hidden p-0'
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className='text-xl'>
            {isEditMode
              ? 'Edit Reminder'
              : isBulkStageVariant || isParticipantsVariant
                ? 'Send Reminder'
                : 'Create New Reminder'}
          </DialogTitle>
        </DialogHeader>

        <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
          <Formik
            key={formKey}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ submitForm, values }) => (
              <DialogBody className='flex min-h-0 flex-1 flex-col overflow-hidden p-0'>
                <div className='flex min-h-0 flex-1 overflow-hidden'>
                  <div className='flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'>
                    <div className='min-h-0 flex-1 space-y-4 overflow-y-auto p-4'>
                      <CreateReminderAudienceSection
                        variant={isEditMode ? 'stage' : variant}
                        bulkStageConfig={bulkStageConfig}
                      />
                      <CreateReminderDetailsSection
                        title={
                          isStageVariant
                            ? '2. Communication Details'
                            : undefined
                        }
                      />
                      {isStageVariant ? (
                        <>
                          <StageReminderDetailsSection />
                          <StageRepetitionSection />
                        </>
                      ) : (
                        <CreateReminderScheduleSection />
                      )}
                    </div>

                    <div className='grid grid-cols-2 shadow-md shrink-0 gap-3 border-t border-primary-100 bg-white px-6 py-4'>
                      <Button
                        type='button'
                        isLoading={isPending}
                        onClick={() => submitForm()}
                      >
                        <Send2
                          className='size-5!'
                          variant='Bold'
                        />
                        {isEditMode
                          ? 'Save Changes'
                          : isStageVariant
                            ? 'Submit Reminder'
                            : values.scheduleDelivery
                              ? 'Schedule Reminder'
                              : 'Send Now'}
                      </Button>
                      <DialogClose asChild>
                        <Button
                          type='button'
                          variant='primaryOutline'
                        >
                          Cancel
                        </Button>
                      </DialogClose>
                    </div>
                  </div>

                  <div className='flex min-h-0 w-[50%] shrink-0 flex-col items-center overflow-y-auto border-l border-primary-100 bg-primary-50 pl-6'>
                    <LivePreview
                      attachments={values.attachments ?? []}
                      channel={values.notification_channel}
                      className='min-h-0 w-full flex-1'
                      content={values.content}
                      subject={values.subject ?? undefined}
                    />
                  </div>
                </div>
              </DialogBody>
            )}
          </Formik>
        </div>
      </DialogContent>
    </Dialog>
  );
}
